import React, { FC, useState } from 'react';
import { Link } from 'react-router-dom';
import classNames from 'classnames';
import styled from 'styled-components';
import Utils from '../../shared/utils';
import ProductCover from '../../shared/common/ProductCover';
import { PreviewButton } from '../../shared/preview/PreviewButton';
import CartPriceActionButton from '../cart/CartPriceActionButton';
import { CampaignProductStatistics } from './CampaignProductStatistics';
import SelectionListProductItemDropDown from '../../shared/common/SelectionListProductItemDropDown';
import ProductSeries from '../../shared/product/ProductSeries';
import ProductAuthors from '../../shared/product/ProductAuthors';
import ProductNarrators from '../../shared/product/ProductNarrators';
import ProductGenres from '../../shared/product/ProductGenres';
import ProductAge from '../../shared/product/ProductAge';
import { ProductFormat } from '../../shared/product/ProductFormat';
import ProductPublisher from '../../shared/product/ProductPublisher';
import { ProductReleaseDate } from '../../shared/product/ProductReleaseDate';
import ProductUsageConditions from '../../shared/product/ProductUsageConditions';
import ProductLanguage from '../../shared/product/ProductLanguage';
import { concreteLoanFormatNameToObject } from '../../store/loanFormat/actions';
import { ListBadges } from '../../shared/common/BadgesV2';
import { CenteredListBadges } from '../../shared/common/BadgesV2/CenteredBadgeContainer';
import CampaignUsageBadge from '../../yourborrowbox/marketing/campaigns/CampaignUsageBadge';
import { ProductListItemProps } from '../../shared/common/ProductList';
import MetadataUtils from '../../shared/utils/utils-metadata';

const StCampaignUsageBadge = styled(CampaignUsageBadge)`
    width: 136px;
`;

type Props = ProductListItemProps;

export const CampaignListItem: FC<Props> = props => {
    const { product, loanFormat, onAddPrice, onProductImageClick, actionButtons } = props;
    const [showDetails, setShowDetails] = useState(false);

    const loanFormatKey = loanFormat;
    const productLoanFormat = concreteLoanFormatNameToObject(product.loanFormat);
    const productLoanFormatKey = productLoanFormat?.key;
    const productLoanFormatSingularName = productLoanFormat?.singularName;

    const packshotClasses = classNames('product-list-cell', 'packshot', 'catalogue', {
        over: showDetails
    });

    const productLink = `/products/${product.id}`;

    const combinedTitleLabel = product.title + (product.subTitle ? ` ${product.subTitle}` : '');

    const productLanguage = (
        <ProductLanguage product={product} loanFormatKey={loanFormatKey} label={product.language!} />
    );

    const isPreOrder = MetadataUtils.isProductPreOrderable(product);

    return (
        <div className="product-list-row">
            <div className={packshotClasses}>
                <Link
                    to={productLink}
                    onClick={e => {
                        e.preventDefault();
                        onProductImageClick();
                    }}
                >
                    <ProductCover
                        onMouseEnter={() => setShowDetails(true)}
                        onMouseLeave={() => setShowDetails(false)}
                        minSizeInPx={288}
                        product={product}
                    />
                </Link>
                <PreviewButton product={product} />
            </div>
            <div className="product-list-cell">
                <div className="product-list-details">
                    <div style={{ display: 'table-row' }}>
                        <div className="tc tcw-25">
                            <ProductSeries
                                loanFormatKey={loanFormatKey}
                                product={{
                                    productClass: product.productClass,
                                    seriesName: product.seriesName,
                                    seriesNumber: product.seriesNumber
                                }}
                            />
                            <p title={combinedTitleLabel} className="label">
                                <Link to={productLink}>
                                    <strong>{Utils.truncate(combinedTitleLabel)}</strong>
                                </Link>
                            </p>
                            <ProductAuthors
                                loanFormatKey={loanFormatKey}
                                product={{ authors: product.authors, productClass: product.productClass }}
                            />
                            <ProductNarrators
                                loanFormatKey={productLoanFormatKey}
                                product={{ productClass: product.productClass, narrators: product.narrators }}
                            />
                        </div>
                        <div className="tc tcw-20">
                            <ProductFormat {...props} loanFormatKey={productLoanFormatKey!} />
                            <ProductGenres {...props} loanFormatKey={loanFormatKey} />
                            <ProductAge {...props} loanFormatKey={loanFormatKey} />
                            {productLanguage}
                        </div>
                        <div className="tc tcw-25">
                            <p>{product.isbn13}</p>
                            <ProductReleaseDate {...props} />
                            <ProductPublisher {...props} loanFormatKey={loanFormatKey} />
                            <ProductUsageConditions product={product} loanFormatKey={loanFormatKey} />
                            <StCampaignUsageBadge
                                loanFormat={productLoanFormatSingularName!}
                                isSimultaneousAccess={product.simultaneousAccess}
                                usageLimit={product.usageLimit}
                                campaignTerm={product.termLimit}
                                releaseDate={product.releaseDate}
                                isPreOrder={isPreOrder}
                            />
                        </div>
                        <div className="tc tcw-20 actions">
                            {actionButtons || null}
                            <SelectionListProductItemDropDown {...props} onAddPrice={onAddPrice} />
                            <CartPriceActionButton {...props} onAddPrice={onAddPrice} />
                        </div>
                    </div>
                </div>
                <div className="product-list-bottom">
                    <CampaignProductStatistics
                        copyStockAmount={product.copyStockAmount}
                        stockDetails={product.stockDetails}
                        preOrderStockDetails={product.preOrderStockDetails}
                        campaignStockAmount={product.campaignStockAmount}
                        loanFormatKey={productLoanFormatKey!}
                        copiesInCart={product.copiesInCart}
                    />
                    {/*//TODO: CENTERING*/}
                    <CenteredListBadges>
                        <ListBadges product={product} loanFormatKey={loanFormatKey} showAll={showDetails} />
                    </CenteredListBadges>
                </div>
            </div>
        </div>
    );
};
